import React, { useEffect, useRef, useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Paper, 
  Stack,
  Card,
  CardContent,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { 
  AutoAwesomeOutlined, 
  ShieldOutlined, 
  SpeedOutlined,
  TrendingUpOutlined,
  CheckCircleOutline,
  ArrowForward,
  ExpandMore,
  PlayArrow,
  Description,
  Psychology,
  AutoAwesome,
  VerifiedUser,
  Bolt,
  CloudUpload,
  Edit,
  Send,
  Check,
  Star,
  StarBorder,
  ArrowRightAlt,
  LightbulbOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Animated background ref
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Prompt state and persistence
  const [prompt, setPrompt] = useState<string>('');

  // Handle prompt change
  const handlePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrompt(e.target.value);
  };

  // Handle example chip click
  const handleExampleClick = (example: string) => {
    setPrompt(example);
  };

  // Handle generate button click
  const handleGenerateClick = () => {
    if (prompt.trim()) {
      // Store prompt in localStorage
      localStorage.setItem('aiPrompt', prompt);
      navigate('/documents/upload?tab=ai');
    }
  };

  // Canvas animation for the hero section
  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set canvas dimensions
    const setCanvasDimensions = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      
      canvas.width = parent.offsetWidth;
      canvas.height = parent.offsetHeight;
    };
    
    setCanvasDimensions();
    window.addEventListener('resize', setCanvasDimensions);
    
    // Particle class
    class Particle {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      color: string;
      
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 3 + 1;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.color = `rgba(255, 255, 255, ${Math.random() * 0.3})`;
      }
      
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        
        if (this.x < 0 || this.x > canvas.width) {
          this.speedX = -this.speedX;
        }
        
        if (this.y < 0 || this.y > canvas.height) {
          this.speedY = -this.speedY;
        }
      }
      
      draw() {
        if (!ctx) return;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // Create particles
    const particles: Particle[] = [];
    const particleCount = Math.min(canvas.width / 10, 100);
    
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }
    
    // Animation loop
    const animate = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      for (const particle of particles) {
        particle.update();
        particle.draw();
      }
      
      requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', setCanvasDimensions);
    };
  }, []);

  // Features section data
  const features = [
    {
      icon: <AutoAwesomeOutlined fontSize="large" color="primary" />,
      title: 'AI-Powered Drafting',
      description: 'Generate precise legal documents in minutes with our advanced AI technology.'
    },
    {
      icon: <ShieldOutlined fontSize="large" color="primary" />,
      title: 'Risk Analysis',
      description: 'Identify potential legal risks and get recommendations for improving contract security.'
    },
    {
      icon: <SpeedOutlined fontSize="large" color="primary" />,
      title: 'Efficiency Boost',
      description: 'Reduce contract review time by up to 80% with automated clause analysis.'
    },
    {
      icon: <TrendingUpOutlined fontSize="large" color="primary" />,
      title: 'Analytics Dashboard',
      description: 'Gain insights into your contract portfolio with comprehensive analytics.'
    }
  ];

  // Testimonials data
  const testimonials = [
    {
      quote: "Contract AI has transformed our legal department's productivity. What used to take days now takes hours.",
      author: "Sarah Johnson",
      position: "Legal Director, TechCorp"
    },
    {
      quote: "The risk analysis feature has helped us identify critical issues that we would have otherwise missed.",
      author: "Michael Chen",
      position: "General Counsel, Innovate Inc."
    },
    {
      quote: "Implementing Contract AI was the best decision we made this year. The ROI has been exceptional.",
      author: "Emily Rodriguez",
      position: "COO, Global Solutions"
    }
  ];

  // Pricing plans
  const plans = [
    {
      name: "Starter",
      price: "$99",
      period: "per month",
      features: [
        "10 AI-generated documents",
        "Basic template library",
        "Email support",
        "1 user"
      ],
      buttonText: "Start Free Trial",
      highlighted: false
    },
    {
      name: "Professional",
      price: "$249",
      period: "per month",
      features: [
        "50 AI-generated documents",
        "Advanced template library",
        "Risk analysis",
        "Analytics dashboard",
        "Priority support",
        "5 users"
      ],
      buttonText: "Start Free Trial",
      highlighted: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      features: [
        "Unlimited AI-generated documents",
        "Full template library",
        "Advanced risk analysis",
        "Custom integrations",
        "Dedicated account manager",
        "Unlimited users"
      ],
      buttonText: "Contact Sales",
      highlighted: false
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box 
        sx={{ 
          minHeight: '90vh',
          background: 'linear-gradient(135deg, #0b1c30 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          pt: 4
        }}
      >
        {/* Animated background */}
        <canvas 
          ref={canvasRef} 
          style={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0.1
          }}
        />
        
        {/* Header */}
        <LandingHeader />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, py: { xs: 4, md: 8 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box>
                <Typography 
                  variant="h1" 
                  component="h1" 
                  fontWeight="800" 
                  gutterBottom
                  sx={{ 
                    fontSize: { xs: '2.5rem', md: '3.75rem' },
                    lineHeight: 1.1,
                    background: 'linear-gradient(90deg, #ffffff 0%, #b3e0ff 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    mb: 3
                  }}
                >
                  AI-Powered Contract Generation & Analysis
                </Typography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: 4, 
                    fontWeight: 400,
                    lineHeight: 1.6,
                    color: 'rgba(255,255,255,0.85)',
                    fontSize: { xs: '1.1rem', md: '1.25rem' }
                  }}
                >
                  Transform your legal workflow with our advanced AI. Generate precise contracts, analyze legal risks, and enhance document clarity in minutes, not days.
                </Typography>
                
                {/* Demo prompt field */}
                <Box 
                  sx={{ 
                    mb: 4, 
                    p: 1.5, 
                    bgcolor: 'rgba(255,255,255,0.07)', 
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.1)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder="Describe the contract you need..."
                    variant="standard"
                    value={prompt}
                    onChange={handlePromptChange}
                    InputProps={{
                      disableUnderline: true,
                      style: { color: 'white', padding: '8px 12px', fontSize: '1.1rem' },
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button 
                            variant="contained" 
                            color="primary"
                            onClick={handleGenerateClick}
                            endIcon={<ArrowForward />}
                            disabled={!prompt.trim()}
                            sx={{ 
                              borderRadius: 2,
                              bgcolor: '#2d8cff',
                              '&:hover': { bgcolor: '#1a7dff' }
                            }}
                          >
                            Generate
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      '& .MuiInputBase-root': {
                        bgcolor: 'transparent',
                        borderRadius: 2
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, px: 1.5 }}>
                    <Typography variant="body2" color="rgba(255,255,255,0.6)" sx={{ mr: 1 }}>
                      Try:
                    </Typography>
                    {['NDA for software development', 'Consulting agreement', 'Employment contract'].map((example, i) => (
                      <Chip 
                        key={i} 
                        label={example} 
                        size="small"
                        onClick={() => handleExampleClick(example)}
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.1)', 
                          color: 'white',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                        }} 
                      />
                    ))}
                  </Box>
                </Box>
                
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={2}
                  sx={{ mb: 4 }}
                >
                  <Button 
                    variant="contained" 
                    size="large" 
                    onClick={() => {
                      navigate('/register');
                      // Scroll to the form section after navigation
                      setTimeout(() => {
                        window.scrollTo(0, 0);
                      }, 100);
                    }}
                    sx={{ 
                      py: 1.5, 
                      px: 4, 
                      borderRadius: 2,
                      fontSize: '1rem',
                      bgcolor: '#2d8cff',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                      '&:hover': {
                        bgcolor: '#1a7dff',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 20px rgba(0,0,0,0.3)',
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Start Free Trial
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="large" 
                    startIcon={<PlayArrow />}
                    onClick={() => window.open('https://www.youtube.com/watch?v=dQw4w9WgXcQ', '_blank')}
                    sx={{ 
                      py: 1.5, 
                      px: 4, 
                      borderRadius: 2,
                      fontSize: '1rem',
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'white',
                      '&:hover': {
                        borderColor: 'white',
                        bgcolor: 'rgba(255,255,255,0.05)'
                      }
                    }}
                  >
                    Watch Demo
                  </Button>
                </Stack>
                
                {/* Trust indicators */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex' }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} sx={{ color: '#ffb400', fontSize: '1.2rem' }} />
                      ))}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'rgba(255,255,255,0.9)' }}>
                      4.9/5 from 500+ reviews
                    </Typography>
                  </Stack>
                  
                  <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleOutline sx={{ color: '#4caf50', mr: 1, fontSize: '1.2rem' }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        No credit card required
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleOutline sx={{ color: '#4caf50', mr: 1, fontSize: '1.2rem' }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Cancel anytime
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
              <Box 
                sx={{ 
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {/* Main interface mockup */}
                <Box 
                  sx={{ 
                    width: '100%',
                    maxWidth: '550px',
                    height: 'auto',
                    bgcolor: '#ffffff',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 20px 80px rgba(0,0,0,0.3)',
                    transform: 'perspective(1500px) rotateY(-5deg) rotateX(5deg)',
                    transition: 'all 0.5s ease',
                    '&:hover': {
                      transform: 'perspective(1500px) rotateY(-2deg) rotateX(2deg) translateY(-10px)',
                    },
                    position: 'relative',
                    zIndex: 2
                  }}
                >
                  {/* Mock editor interface */}
                  <Box sx={{ bgcolor: '#f5f9ff', p: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight="600" color="#1a365d">Contract Editor</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip label="AI Assist" size="small" icon={<AutoAwesome sx={{ fontSize: '0.8rem' }} />} sx={{ bgcolor: '#e3f2fd', color: '#0277bd' }} />
                    </Box>
                  </Box>
                  <Box sx={{ p: 3, height: '350px', overflow: 'hidden' }}>
                    <Typography variant="h6" color="#1a365d" gutterBottom>Non-Disclosure Agreement</Typography>
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
                      This Non-Disclosure Agreement ("Agreement") is entered into as of [DATE] by and between [PARTY A], with offices at [ADDRESS] ("Disclosing Party") and [PARTY B], with offices at [ADDRESS] ("Receiving Party").
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', my: 2, p: 1.5, bgcolor: '#f0f7ff', borderRadius: 1, border: '1px dashed #90caf9' }}>
                      <LightbulbOutlined sx={{ color: '#1976d2', mr: 1.5, fontSize: '1.2rem' }} />
                      <Typography variant="body2" color="#1976d2" sx={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                        AI Suggestion: Consider adding a specific definition of confidential information to strengthen legal protection.
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
                      1. Definition of Confidential Information. "Confidential Information" means any information disclosed by Disclosing Party to Receiving Party, either directly or indirectly, in writing, orally or by inspection of tangible objects...
                    </Typography>
                  </Box>
                </Box>
                
                {/* Floating elements */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '15%',
                    left: '-5%',
                    bgcolor: 'white',
                    color: '#1a365d',
                    p: 2,
                    borderRadius: 2,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                    maxWidth: '220px',
                    zIndex: 3,
                    animation: 'float 6s ease-in-out infinite',
                    '@keyframes float': {
                      '0%': { transform: 'translateY(0px)' },
                      '50%': { transform: 'translateY(-10px)' },
                      '100%': { transform: 'translateY(0px)' }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AutoAwesome sx={{ color: '#2d8cff', mr: 1, fontSize: '1.2rem' }} />
                    <Typography variant="subtitle2" fontWeight="bold" color="#1a365d">AI Risk Analysis</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>Identified 3 potential legal issues in this contract</Typography>
                </Box>
                
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '10%',
                    right: '0%',
                    bgcolor: 'white',
                    color: '#1a365d',
                    p: 2,
                    borderRadius: 2,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                    maxWidth: '220px',
                    zIndex: 1,
                    animation: 'float 6s ease-in-out infinite 1.5s',
                    '@keyframes float': {
                      '0%': { transform: 'translateY(0px)' },
                      '50%': { transform: 'translateY(-10px)' },
                      '100%': { transform: 'translateY(0px)' }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Bolt sx={{ color: '#ff9800', mr: 1, fontSize: '1.2rem' }} />
                    <Typography variant="subtitle2" fontWeight="bold" color="#1a365d">Smart Suggestions</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>AI-powered recommendations to improve contract clarity</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
        
        {/* Wave shape divider */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -1,
            left: 0,
            width: '100%',
            overflow: 'hidden',
            lineHeight: 0,
            zIndex: 1,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            style={{
              position: 'relative',
              display: 'block',
              width: 'calc(100% + 1.3px)',
              height: '60px',
              transform: 'rotateY(180deg)'
            }}
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              fill="#ffffff"
            ></path>
          </svg>
        </Box>
      </Box>
      
      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 8, mt: { xs: 0, md: -10 }, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <Paper 
              elevation={6} 
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 4,
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 30px rgba(0,0,0,0.08)'
                }
              }}
            >
              <Typography variant="h3" fontWeight="bold" color="primary">80%</Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>Time Saved on Contract Review</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper 
              elevation={6} 
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 4,
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 30px rgba(0,0,0,0.08)'
                }
              }}
            >
              <Typography variant="h3" fontWeight="bold" color="primary">10,000+</Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>Documents Generated Monthly</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper 
              elevation={6} 
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 4,
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 30px rgba(0,0,0,0.08)'
                }
              }}
            >
              <Typography variant="h3" fontWeight="bold" color="primary">99.9%</Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>Accuracy Rate</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
      
      {/* Features Section */}
      <Box id="features" sx={{ bgcolor: '#f8faff', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h2" 
              component="h2" 
              fontWeight="800" 
              gutterBottom
              sx={{ 
                fontSize: { xs: '2rem', md: '2.75rem' },
                background: 'linear-gradient(90deg, #1a365d 0%, #2d8cff 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                mb: 2
              }}
            >
              Transform Your Legal Workflow
            </Typography>
            <Typography 
              variant="subtitle1" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '700px', 
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.6
              }}
            >
              Our AI-powered platform streamlines every aspect of contract creation, analysis, and management
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative', height: '100%' }}>
                <Box 
                  sx={{ 
                    position: 'relative',
                    height: { xs: '300px', md: '500px' },
                    width: '100%',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(180deg, rgba(26,54,93,0) 0%, rgba(26,54,93,0.8) 100%)',
                      zIndex: 1
                    }
                  }}
                >
                  <Box 
                    component="img"
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                    alt="Contract AI Editor"
                    sx={{ 
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  
                  <Box sx={{ 
                    position: 'absolute', 
                    bottom: 0, 
                    left: 0, 
                    width: '100%', 
                    p: 4,
                    zIndex: 2
                  }}>
                    <Typography variant="h5" fontWeight="bold" color="white" gutterBottom>
                      AI-Powered Document Editor
                    </Typography>
                    <Typography variant="body1" color="rgba(255,255,255,0.9)">
                      Edit, enhance, and analyze your contracts with our intuitive interface
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                {features.map((feature, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 3, 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        borderRadius: 3,
                        border: '1px solid rgba(0,0,0,0.05)',
                        transition: '0.3s',
                        '&:hover': {
                          boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                          transform: 'translateY(-5px)',
                          borderColor: 'primary.main'
                        }
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: 2,
                        '& .MuiSvgIcon-root': {
                          fontSize: '2rem',
                          color: '#2d8cff'
                        }
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#1a365d' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {feature.description}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto', pt: 2 }}>
                        <Typography 
                          variant="body2" 
                          color="primary" 
                          fontWeight="500"
                          onClick={() => {
                            // Determine which feature parameter to pass based on the feature title
                            const featureParam = feature.title === 'AI-Powered Drafting' ? 'ai-generation' :
                              feature.title === 'Risk Analysis' ? 'smart-editing' :
                              feature.title === 'Efficiency Boost' ? 'upload' :
                              feature.title === 'Analytics Dashboard' ? 'upload' : '';
                            
                            navigate(`/register?feature=${featureParam}`);
                            // Scroll to top after navigation
                            window.scrollTo(0, 0);
                          }}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          Learn more
                          <ArrowRightAlt sx={{ ml: 0.5, fontSize: '1rem' }} />
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
          
          {/* Feature highlights */}
          <Box sx={{ mt: 8, textAlign: 'center' }}>
            <Grid container spacing={3} justifyContent="center">
              <Grid item xs={12} sm={4}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: '0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)'
                    }
                  }}
                  onClick={() => {
                    navigate('/register?feature=upload');
                    // Scroll to top after navigation
                    window.scrollTo(0, 0);
                  }}
                >
                  <CloudUpload sx={{ fontSize: 40, color: '#2d8cff', mb: 2 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Upload Documents</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Easily upload and analyze your existing contracts
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: '0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)'
                    }
                  }}
                  onClick={() => {
                    navigate('/register?feature=ai-generation');
                    // Scroll to top after navigation
                    window.scrollTo(0, 0);
                  }}
                >
                  <AutoAwesome sx={{ fontSize: 40, color: '#2d8cff', mb: 2 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>AI Generation</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create contracts from scratch with simple prompts
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: '0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)'
                    }
                  }}
                  onClick={() => {
                    navigate('/register?feature=smart-editing');
                    // Scroll to top after navigation
                    window.scrollTo(0, 0);
                  }}
                >
                  <Edit sx={{ fontSize: 40, color: '#2d8cff', mb: 2 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Smart Editing</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Powerful editor with AI-assisted improvements
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
      
      {/* How It Works Section */}
      <Box id="how-it-works" sx={{ bgcolor: '#0b1c30', py: { xs: 8, md: 12 }, color: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h2" 
              component="h2" 
              fontWeight="800" 
              gutterBottom
              sx={{ 
                fontSize: { xs: '2rem', md: '2.75rem' },
                background: 'linear-gradient(90deg, #ffffff 0%, #b3e0ff 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                mb: 2
              }}
            >
              How Contract AI Works
            </Typography>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                maxWidth: '700px', 
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.6,
                color: 'rgba(255,255,255,0.8)'
              }}
            >
              Get started in minutes with our intuitive workflow
            </Typography>
          </Box>
          
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <Box 
                  sx={{ 
                    position: 'relative',
                    width: '100%',
                    height: { xs: '300px', md: '450px' },
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 20px 80px rgba(0,0,0,0.3)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    bgcolor: '#1a365d',
                    p: 2
                  }}
                >
                  {/* Mock UI for workflow demonstration */}
                  <Box sx={{ height: '100%', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
                    <Box 
                      component="img"
                      src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                      alt="Contract AI Workflow"
                      sx={{ 
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    
                    {/* Overlay with step indicators */}
                    <Box sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      left: 0, 
                      width: '100%', 
                      height: '100%',
                      background: 'linear-gradient(135deg, rgba(26,54,93,0.7) 0%, rgba(45,140,255,0.4) 100%)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      p: 4
                    }}>
                      <Typography variant="h4" fontWeight="bold" color="white" gutterBottom>
                        Streamlined Workflow
                      </Typography>
                      <Typography variant="body1" color="rgba(255,255,255,0.9)" textAlign="center" sx={{ mb: 4 }}>
                        Our AI-powered platform makes contract creation and management effortless
                      </Typography>
                      
                      <Button 
                        variant="contained" 
                        size="large"
                        startIcon={<PlayArrow />}
                        onClick={() => window.open('https://www.youtube.com/watch?v=dQw4w9WgXcQ', '_blank')}
                        sx={{ 
                          bgcolor: 'white', 
                          color: '#1a365d',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                        }}
                      >
                        Watch Demo
                      </Button>
                    </Box>
                  </Box>
                </Box>
                
                {/* Floating elements */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '10%',
                    right: '-5%',
                    bgcolor: 'white',
                    color: '#1a365d',
                    p: 2,
                    borderRadius: 2,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                    maxWidth: '180px',
                    zIndex: 3,
                    animation: 'float 6s ease-in-out infinite 0.5s',
                    '@keyframes float': {
                      '0%': { transform: 'translateY(0px)' },
                      '50%': { transform: 'translateY(-10px)' },
                      '100%': { transform: 'translateY(0px)' }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CloudUpload sx={{ color: '#2d8cff', mr: 1, fontSize: '1.2rem' }} />
                    <Typography variant="subtitle2" fontWeight="bold" color="#1a365d">Step 1</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>Upload or create a new document</Typography>
                </Box>
                
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '15%',
                    left: '-5%',
                    bgcolor: 'white',
                    color: '#1a365d',
                    p: 2,
                    borderRadius: 2,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                    maxWidth: '180px',
                    zIndex: 3,
                    animation: 'float 6s ease-in-out infinite 1.5s',
                    '@keyframes float': {
                      '0%': { transform: 'translateY(0px)' },
                      '50%': { transform: 'translateY(-10px)' },
                      '100%': { transform: 'translateY(0px)' }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Edit sx={{ color: '#2d8cff', mr: 1, fontSize: '1.2rem' }} />
                    <Typography variant="subtitle2" fontWeight="bold" color="#1a365d">Step 3</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>Edit and enhance with AI</Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Stack spacing={5}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      bgcolor: 'rgba(45,140,255,0.1)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mr: 3,
                      flexShrink: 0,
                      border: '1px solid rgba(45,140,255,0.3)'
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold" color="#2d8cff">1</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="white" gutterBottom>
                      Choose Your Method
                    </Typography>
                    <Typography variant="body1" color="rgba(255,255,255,0.8)" sx={{ lineHeight: 1.7 }}>
                      Upload an existing document, select from our template library, or generate a new contract with AI by describing what you need.
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      bgcolor: 'rgba(45,140,255,0.1)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mr: 3,
                      flexShrink: 0,
                      border: '1px solid rgba(45,140,255,0.3)'
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold" color="#2d8cff">2</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="white" gutterBottom>
                      AI Analysis & Enhancement
                    </Typography>
                    <Typography variant="body1" color="rgba(255,255,255,0.8)" sx={{ lineHeight: 1.7 }}>
                      Our AI analyzes your document for legal risks and suggests improvements to strengthen your contract's clarity and enforceability.
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      bgcolor: 'rgba(45,140,255,0.1)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mr: 3,
                      flexShrink: 0,
                      border: '1px solid rgba(45,140,255,0.3)'
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold" color="#2d8cff">3</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="white" gutterBottom>
                      Edit & Customize
                    </Typography>
                    <Typography variant="body1" color="rgba(255,255,255,0.8)" sx={{ lineHeight: 1.7 }}>
                      Use our intuitive editor to make changes, accept AI suggestions, and customize the document to your specific requirements.
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      bgcolor: 'rgba(45,140,255,0.1)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mr: 3,
                      flexShrink: 0,
                      border: '1px solid rgba(45,140,255,0.3)'
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold" color="#2d8cff">4</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="white" gutterBottom>
                      Export & Share
                    </Typography>
                    <Typography variant="body1" color="rgba(255,255,255,0.8)" sx={{ lineHeight: 1.7 }}>
                      Download your finalized document in multiple formats (PDF, DOCX, TXT) or share it securely with stakeholders for review and signature.
                  </Typography>
                </Box>
              </Box>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Testimonials Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" fontWeight="bold" textAlign="center" gutterBottom>
            What Our Clients Say
          </Typography>
          <Typography variant="subtitle1" textAlign="center" color="text.secondary" sx={{ mb: 6, maxWidth: '700px', mx: 'auto' }}>
            Trusted by legal professionals at leading companies worldwide
          </Typography>
          
          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body1" sx={{ mb: 3, flexGrow: 1, fontStyle: 'italic' }}>
                      "{testimonial.quote}"
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle2" fontWeight="bold">
                      {testimonial.author}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {testimonial.position}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* Pricing Section */}
      <Container id="pricing" maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" component="h2" fontWeight="bold" textAlign="center" gutterBottom>
          Simple, Transparent Pricing
        </Typography>
        <Typography variant="subtitle1" textAlign="center" color="text.secondary" sx={{ mb: 6, maxWidth: '700px', mx: 'auto' }}>
          Choose the plan that works best for your needs
        </Typography>
        
        <Grid container spacing={4} justifyContent="center">
          {plans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                elevation={plan.highlighted ? 8 : 1}
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  position: 'relative',
                  transition: '0.3s',
                  transform: plan.highlighted ? 'scale(1.05)' : 'none',
                  '&:hover': {
                    transform: plan.highlighted ? 'scale(1.07)' : 'scale(1.02)'
                  }
                }}
              >
                {plan.highlighted && (
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: 20, 
                      right: 0,
                      bgcolor: 'secondary.main',
                      color: 'white',
                      py: 0.5,
                      px: 2,
                      borderTopLeftRadius: 4,
                      borderBottomLeftRadius: 4,
                      fontWeight: 'bold',
                      fontSize: '0.875rem'
                    }}
                  >
                    Most Popular
                  </Box>
                )}
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {plan.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 3 }}>
                    <Typography variant="h3" fontWeight="bold">
                      {plan.price}
                    </Typography>
                    {plan.period && (
                      <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 1 }}>
                        {plan.period}
                      </Typography>
                    )}
                  </Box>
                  
                  <Divider sx={{ mb: 3 }} />
                  
                  <Stack spacing={2} sx={{ mb: 4, flexGrow: 1 }}>
                    {plan.features.map((feature, i) => (
                      <Box key={i} display="flex" alignItems="center">
                        <CheckCircleOutline color="primary" fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                  </Stack>
                  
                  <Button 
                    variant={plan.highlighted ? "contained" : "outlined"} 
                    color="primary"
                    fullWidth
                    size="large"
                    onClick={() => navigate(plan.name === 'Enterprise' ? '/contact' : '/register')}
                  >
                    {plan.buttonText}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
      
      {/* CTA Section */}
      <Box 
        sx={{ 
          background: 'linear-gradient(120deg, #2196f3 0%, #3f51b5 100%)',
          color: 'white',
          py: 8,
          mt: 8
        }}
      >
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Ready to Transform Your Legal Workflow?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, fontWeight: 300, opacity: 0.9 }}>
              Join thousands of legal professionals who are saving time and reducing risk with Contract AI.
            </Typography>
            <Button 
              variant="contained" 
              size="large" 
              onClick={() => navigate('/register')}
              sx={{ 
                bgcolor: '#6200ea',
                color: 'white',
                py: 1.5,
                px: 4,
                '&:hover': {
                  bgcolor: '#7c4dff'
                }
              }}
            >
              Start Your Free Trial
            </Button>
          </Box>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};

export default LandingPage;
